// lists.js — canonical timer lists + ensure-4-lists logic (plan S4/S5).
//
// Exact display names are a contract (plan §5a, guardrail 11): the bot's
// scheduler maps list NAME -> duration, so the web app must create and use
// exactly these four names and nothing else. The eternity list display
// name is the single sanctioned non-ASCII name (guardrail 13).

export const ETERNITY_NAME = '\u267E\uFE0F'; // the eternal list name

export const LIST_DEFS = [
  { name: 'T-1', durationMs: 1 * 24 * 60 * 60 * 1000, purpose: 'app.bsky.graph.defs#modlist' },
  { name: 'T-7', durationMs: 7 * 24 * 60 * 60 * 1000, purpose: 'app.bsky.graph.defs#modlist' },
  { name: 'T-30', durationMs: 30 * 24 * 60 * 60 * 1000, purpose: 'app.bsky.graph.defs#modlist' },
  { name: ETERNITY_NAME, durationMs: null, purpose: 'app.bsky.graph.defs#modlist' },
];

// name -> duration (ms), eternity = null (never due). Mirror of the bot's
// scheduler duration map (plan §13).
// 2026-09-07 owner: T-90 removed, T-1 added (1-day list).
export function durationForListName(name) {
  const def = LIST_DEFS.find((d) => d.name === name);

  return def ? def.durationMs : undefined;
}

// Pure: given the lists an account already has, decide what is missing.
// existingLists: array of { name }.
export function ensureListsPlan(existingLists) {
  const names = existingLists.map((l) => l.name);
  const missing = LIST_DEFS.flatMap((d) => (names.includes(d.name) ? [] : [d.name]));
  const duplicateNames = [...new Set(names.filter((n, i) => names.indexOf(n) !== i))];

  return { missing, duplicates: duplicateNames };
}

const LIST_DESCRIPTION =
  "created by timer.psingletary.com a website and a bot for timebound mutes and blocks";

function listRecord(def, avatarBlob) {
  const record = {
    purpose: def.purpose,
    name: def.name,
    description: LIST_DESCRIPTION,
    createdAt: new Date().toISOString(),
  };

  if (avatarBlob) record.avatar = avatarBlob;

  return record;
}

// Bot-account avatar URL (timer.psingletary.com) — used as the avatar for
// newly created lists so they carry the product's identity.
export const TIMER_AVATAR_URL =
  "https://cdn.bsky.app/img/avatar/plain/did:plc:wmcctdshlugzrzqsg5n72fcs/bafkreidz53iwjhxnmtobwm2xs6b3briqigvyxy4loolbatlw6uk2i4gjgq";

// Upload the timer avatar as a blob on the user's PDS and return the
// blob-ref for a list record's avatar field.
export async function timerAvatarBlob(agent) {
  const res = await fetch(TIMER_AVATAR_URL);

  if (!res.ok) throw new Error(`Failed to fetch timer avatar: ${res.status}`);
  const bytes = new Uint8Array(await res.arrayBuffer());

  const upload = await agent.com.atproto.repo.uploadBlob(bytes, {
    encoding: "image/webp", // the cdn avatar is webp (verified content-type)
  });

  return upload.data.blob;
}

// Agent-bound: create any missing canonical lists on the account.
// Returns { created: [{name, uri}], missing, duplicates }. Does NOT delete
// or rename anything — duplicates are reported for the owner, never auto-
// resolved (guardrail: exact-name contract, but no destructive writes).
export async function ensureListsOnAccount(agent, did) {
  const res = await agent.app.bsky.graph.getLists({ actor: did, limit: 100 });
  const plan = ensureListsPlan(res.data.lists.map((l) => ({ name: l.name })));

  // Upload the timer avatar once, reuse for every list created this run.
  let avatarBlob = null;

  if (plan.missing.length > 0) {
    try {
      avatarBlob = await timerAvatarBlob(agent);
    } catch (avatarErr) {
      console.error("Avatar upload failed (creating lists without avatar):", avatarErr);
    }
  }

  const created = [];

  for (const name of plan.missing) {
    const def = LIST_DEFS.find((d) => d.name === name);

    const rec = await agent.com.atproto.repo.createRecord({
      repo: did,
      collection: "app.bsky.graph.list",
      record: listRecord(def, avatarBlob),
    });

    created.push({ name, uri: rec.data.uri });
  }

  return { created, missing: plan.missing, duplicates: plan.duplicates };
}